import { useContext, useEffect, useState } from 'react';
import keyBy from 'lodash/keyBy';
import { AppConstantsContext } from '../../../context';
import { AsyncCheckoutService } from '../../../services';
// Adjust path as needed

export interface PriceData {
  oldPrice: string | null;
  price: string;
  productId: string | number;
  isYearly: boolean;
  isRecurring: boolean;
  hasDiscount: boolean;
}

export interface PriceDataMap {
  [key: string]: PriceData;
}

const getCurrency = (formattedPrice: string) => {
  const matches = formattedPrice.match(/^([€$]|[A-Z]{3})\s*(\d+(?:\.\d{2})?)/);
  if (!matches) return formattedPrice;
  return matches[1];
};
const getAmount = (formattedPrice: string) => {
  const matches = formattedPrice.match(/^([€$]|[A-Z]{3})\s*(\d+(?:\.\d{2})?)/);
  if (!matches) return formattedPrice;
  return matches[2];
};

const getPricePerMonth = (formattedPrice: string, isYearly: boolean) => {
  if (isYearly) {
    const currency = getCurrency(formattedPrice);
    const amount = getAmount(formattedPrice);
    const yearlyPrice = parseFloat(amount) / 12;
    return `${currency}${yearlyPrice.toFixed(2)}`;
  }
  return formattedPrice;
};

const getDiscountedPrice = (formattedPrice: string, discount: number = 0) => {
  const currency = getCurrency(formattedPrice);
  const amount = getAmount(formattedPrice);

  const originalPrice = parseFloat(amount);
  const discountedPrice = originalPrice * (1 - discount / 100);

  return `${currency}${discountedPrice.toFixed(2)}`;
};

export const usePricing = (discount: number = 0, discountedSubscriptions: string[] = []) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { subscriptionIds } = useContext(AppConstantsContext);
  const [priceData, setPriceData] = useState<PriceDataMap>(null);

  const fetchPrice = async () => {
    setLoading(true);
    try {
      const filteredDiscountedSubscriptionIds = Object.values(subscriptionIds).filter(
        (subscriptionId) => discountedSubscriptions.includes(subscriptionId),
      );

      const pricesArray = await Promise.all(
        Object.values(subscriptionIds).map(async (subscriptionId: string) => {
          const p = await AsyncCheckoutService.getPriceInfo(subscriptionId);
          const isDiscountedPrice = filteredDiscountedSubscriptionIds.includes(subscriptionId);

          const isYearly = p.recurring?.subscription?.type === 'year';

          let price = p.price.gross;
          if (isDiscountedPrice) {
            price = getDiscountedPrice(p.price.gross, discount);
          }
          return {
            productId: p.productId,
            oldPrice: isDiscountedPrice ? getPricePerMonth(p.price.gross, isYearly) : null,
            price: getPricePerMonth(price, isYearly),
            isYearly,
            isRecurring: !!p.recurring,
            hasDiscount: isDiscountedPrice,
          };
        }),
      );
      const prices = keyBy(pricesArray, 'productId');
      setPriceData(prices);
    } catch (e) {
      console.error('Error fetching prices:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrice();
  }, []);

  return { priceData, loading, error };
};

export default usePricing;
